import { useState } from 'react';
import type { ReactNode } from 'react';
import { Input, Segmented, Typography } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import TimeSelect from './TimeSelect';
import type { OffDateMode } from '../../../types/provider';

const { Text } = Typography;

interface Props {
  date: string;
  reason: string;
  mode: OffDateMode;
  startTime: string | null;
  endTime: string | null;
  saving: boolean;
  onDateChange: (date: string) => void;
  onReasonChange: (reason: string) => void;
  onModeChange: (mode: OffDateMode) => void;
  onStartTimeChange: (t: string | null) => void;
  onEndTimeChange: (t: string | null) => void;
  onSubmit: () => void;
}

export default function OffDateAddCard({
  date,
  reason,
  mode,
  startTime,
  endTime,
  saving,
  onDateChange,
  onReasonChange,
  onModeChange,
  onStartTimeChange,
  onEndTimeChange,
  onSubmit,
}: Props) {
  const { t } = useTranslation('provider');
  const [pressed, setPressed] = useState(false);
  const disabled = !date || saving;

  const fullLabel = t('schedule.offDates.fullDay');
  const partialLabel = t('schedule.offDates.partial');

  return (
    <div
      style={{
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        borderRadius: 16,
        padding: '14px 14px 14px',
        marginBottom: 12,
        boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.04))',
      }}
    >
      {/* Date */}
      <FieldLabel>{t('schedule.offDates.dateLabel')}</FieldLabel>
      <input
        type="date"
        value={date}
        min={dayjs().format('YYYY-MM-DD')}
        onChange={(e) => onDateChange(e.target.value)}
        style={{
          width: '100%',
          height: 40,
          padding: '0 12px',
          border: '1px solid var(--bl-divider, #e5e7eb)',
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 500,
          color: date ? 'var(--bl-text-strong, #0f172a)' : '#bfbfbf',
          background: 'var(--bl-surface-soft, #f7f8fa)',
          outline: 'none',
          WebkitAppearance: 'none',
          boxSizing: 'border-box',
          marginBottom: 10,
          fontFamily: 'inherit',
        }}
      />

      {/* Mode */}
      <FieldLabel>{t('schedule.offDates.typeLabel')}</FieldLabel>
      <div style={{ marginBottom: 10 }}>
        <Segmented
          block
          options={[fullLabel, partialLabel]}
          value={mode === 'full' ? fullLabel : partialLabel}
          onChange={(val) => {
            const next: OffDateMode = val === partialLabel ? 'partial' : 'full';
            onModeChange(next);
            if (next === 'full') {
              onStartTimeChange(null);
              onEndTimeChange(null);
            }
          }}
        />
      </div>

      {/* Partial time range */}
      {mode === 'partial' && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <FieldLabel>{t('schedule.offDates.startLabel')}</FieldLabel>
            <TimeSelect
              value={startTime}
              onChange={onStartTimeChange}
              placeholder={t('schedule.offDates.startPlaceholder')}
            />
          </div>
          <Text style={{ flexShrink: 0, paddingBottom: 10, color: 'var(--bl-text-muted, #9ca3af)' }}>→</Text>
          <div style={{ flex: 1 }}>
            <FieldLabel>{t('schedule.offDates.endLabel')}</FieldLabel>
            <TimeSelect
              value={endTime}
              onChange={onEndTimeChange}
              placeholder={t('schedule.offDates.endPlaceholder')}
            />
          </div>
        </div>
      )}

      {/* Reason */}
      <FieldLabel>{t('schedule.offDates.reasonLabel')}</FieldLabel>
      <Input
        value={reason}
        onChange={(e) => onReasonChange(e.target.value)}
        placeholder={t('schedule.offDates.reasonPlaceholder')}
        style={{
          borderRadius: 10,
          height: 40,
          background: 'var(--bl-surface-soft, #f7f8fa)',
          marginBottom: 12,
        }}
      />

      {/* Submit button */}
      <button
        type="button"
        onClick={() => !disabled && onSubmit()}
        disabled={disabled}
        onTouchStart={() => !disabled && setPressed(true)}
        onTouchEnd={() => setPressed(false)}
        onTouchCancel={() => setPressed(false)}
        onMouseDown={() => !disabled && setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onMouseLeave={() => setPressed(false)}
        style={{
          width: '100%',
          minHeight: 44,
          padding: '0 16px',
          border: 'none',
          borderRadius: 12,
          background: disabled
            ? 'var(--bl-surface-soft, #f0f2f5)'
            : 'linear-gradient(135deg, #3390ec 0%, #1677ff 100%)',
          color: disabled ? 'var(--bl-text-muted, #9ca3af)' : '#fff',
          fontSize: 14,
          fontWeight: 700,
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow: disabled
            ? 'none'
            : '0 4px 12px rgba(22,119,255,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
          transform: pressed && !disabled ? 'scale(0.985)' : 'scale(1)',
          transition: 'transform 0.1s ease, box-shadow 0.15s ease',
          WebkitTapHighlightColor: 'transparent',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          outline: 'none',
          letterSpacing: 0.1,
          fontFamily: 'inherit',
        }}
      >
        {saving ? <LoadingOutlined spin /> : <PlusOutlined />}
        <span>{t('schedule.offDates.addBtn')}</span>
      </button>
    </div>
  );
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <Text
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--bl-text-muted, #9ca3af)',
        display: 'block',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 5,
      }}
    >
      {children}
    </Text>
  );
}
